/**
 * BeastLife — CleverTap events (drop-in theme file)
 * ---------------------------------------------------------------------------
 * File:    assets/bl-clevertap-events.js
 * Install: layout/theme.liquid, just before </body>:
 *
 *   <script src="{{ 'bl-clevertap-events.js' | asset_url }}" defer="defer"></script>
 *
 * Transport: clevertap.event.push('<Sheet Event Name>', { '<Sheet Property>': value })
 *   The CleverTap Shopify app (acct 944-6K7-787Z, region in1) already loads the
 *   Web SDK, so window.clevertap exists. Pushes made before the SDK finishes
 *   loading are queued by the standard array shim and flushed on load.
 *
 * Event names and property names are EXACTLY the client sheet's Title Case
 * strings. CleverTap event names are case-sensitive; do not "normalise" them.
 *
 * What this file does NOT do (see the implementation guide):
 *   - Checkout / address / payment / order events → GoKwik checkout (own domain)
 *   - Post-purchase logistics, refunds, repeat purchase → Shopify webhooks
 *   - OTP Sent / Verified / Failed → inside the KwikPass iframe, not observable
 *
 * Data sources it leans on (all verified on beastlife.in, Sept 2026):
 *   __wzrk_product_json          CleverTap app's full product JSON on PDP (compare_at_price, tags…)
 *   ShopifyAnalytics.meta        product / page ids, selected variant
 *   /cart.js + /cart/*.js        cart snapshot; every mutation is intercepted (fetch + XHR)
 *   KwikPass                     'user-loggedin' / 'kpPopup' window events, kp_customer_id / kp-login-no
 *   KwikCart (GoKwik drawer)     openGokwikSideCart / gokwikCheckoutClickFn globals, hits.gokwik.co events
 *   Searchtap                    .st-* result DOM
 *   Judge.me                     .jdgm-* widget
 *   EasyGift                     #easygift-shop config, "_Gifted" line-item property
 *
 * Debug: ?bl_debug=1 → console logs every push and every mirrored GoKwik event.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__blCtBound) return;
  window.__blCtBound = true;
  if (window.Shopify && window.Shopify.designMode) return;

  /* ======================================================================
   * CONFIG
   * ==================================================================== */
  var CONFIG = {
    // 'file'   → this file sends Product Viewed / Added to Cart / Cart Item Removed /
    //            Product Searched with the sheet's full properties. Disable the
    //            equivalent Web SDK events in CleverTap → Settings → Shopify →
    //            Customize Events first, or you get near-duplicates.
    // 'plugin' → this file skips those four; the CleverTap app's versions stay.
    pluginOverlap: 'file',

    // Push KwikPass phone login into CleverTap identity (onUserLogin) so
    // storefront events attach to the same profile the order webhooks hit.
    identifyOnKwikPassLogin: true,
    phoneCountryCode: '+91',

    // Mirror GoKwik cart-drawer analytics (hits.gokwik.co) into sheet events.
    mirrorGokwikCartEvents: true,

    // Tags that identify a product category on this store (product.type is empty).
    categoryTags: ['Protein', 'Creatine', 'BCAA', 'EAA', 'Pre Workout', 'Pre-Workout', 'Mass Gainer', 'Wellness', 'Multivitamin', 'Omega', 'Glutamine', 'Whey', 'Peanut Butter', 'Oats'],

    viewThreshold: 0.5,
    debug: false
  };

  var DEBUG = CONFIG.debug || /[?&]bl_debug=1/.test(location.search);

  /* ======================================================================
   * SELECTORS — every site-specific hook lives here
   * ==================================================================== */
  var SEL = {
    header: '.shopify-section-group-header-group, header, sticky-header',
    footer: 'footer, .shopify-section-group-footer-group',

    // Cards (Dawn + Searchtap)
    productCard: '.product-card-wrapper, .card-wrapper, .st-product-card, [data-product-card]',
    cardTitleLink: '.card__heading a, a[id^="CardLink-"], .st-product-title a, a[href*="/products/"]',
    cardListItem: 'li.grid__item, .grid__item, .swiper-slide, .slick-slide, .st-column, li',
    cardAtc: '.quick-add__submit, .st-atc-btn, form[action*="/cart/add"] [type="submit"], [name="add"]',
    cardPriceSale: '.price__sale .price-item--sale, .st-sale-price',
    cardPriceRegular: '.price__regular .price-item--regular, .price-item--regular, .st-main-price',

    // Categories
    category: 'a.bl-cat-item, a.sbf__card, .menuitmes a, .custom-mobile-drawer .singleitem a, .shopify-section-group-header-group a[href*="/collections/"], .fc-tab-btn',

    // Homepage offers
    banner: 'a.gtm-banner-click',
    bannerSlide: '.homepage-slider a.gtm-banner-click',
    ticker: '.bl-ticker .bl-ti',
    promoBlock: '.bprom__grnt, .bprom__msn, [class*="bprom__"] a',

    // Trust
    qualityCerts: '.cert-box, .cert-item',
    pressLogos: '.wbl__ft',
    guarantee: '.bprom__grnt, [class*="bprom__grnt"]',
    pdpCerts: '.certifications, .footericons',
    labReportLink: 'a[href*="/pages/lab-reports"], a[href*="lab-report"], a[href*="lab-test"]',

    // PDP
    mediaGallery: 'media-gallery',
    mediaItem: '.product__media-item',
    thumbnail: '.thumbnail-list__item',
    flavourSwatch: '.color-swatch .swatch-item a',
    weightSwatch: '.weight-swatch .swatch-item a',
    weightLabel: '.ws-weight',
    variantInputs: 'variant-selects select, variant-radios input[type="radio"], select[name="id"]',
    quantityInput: 'quantity-input input[name="quantity"], input[name="quantity"]',
    pdpAtc: 'product-form form[action*="/cart/add"] [type="submit"], .product-form__submit',
    stickyAtc: '[id*="sticky_atc"] [type="submit"], [id*="sticky_atc"] button',
    buyNow: '.gokwik-buy-now, [class*="buy-now"], button[onclick*="gokwikBuyNow"], .shopify-payment-button__button',
    accordionButton: 'button.plusaccordion, details summary',
    keyBenefits: '.key-benefits',
    aboutSection: '.foraboutus, [id*="aboutthe_product"]',
    aplusSection: '.aplus-images',
    pincodeInput: '#pincode',
    pincodeButton: '.pincode-container button',
    pincodeResult: '#result',
    relatedSection: '.related-products, [id$="__related-products"]',
    backInStock: '[class*="back-in-stock"] form, [id*="back-in-stock"] form, [class*="notify-me"] form, form[class*="restock"]',

    // Reviews (Judge.me)
    reviewsWidget: '#judgeme_product_reviews, .jdgm-review-widget',
    reviewsData: '.jdgm-rev-widg, .jdgm-prev-badge',
    reviewWrite: '.jdgm-write-rev-link',
    reviewForm: '.jdgm-form, form.jdgm-form',
    reviewRating: 'input[name="review[rating]"], .jdgm-form input[type="hidden"][name*="rating"]',
    ratingBreakdown: '.jdgm-histogram, .jdgm-rev-widg__summary-stars, .jdgm-rev-widg__summary',

    // Search (Searchtap)
    searchInput: '.st-search-input, input[name="q"], input[type="search"]',
    searchResults: '.st-results-list, .template-search__results',
    searchResultCard: '.st-product-card, .template-search__results .product-card-wrapper',
    searchNoResults: '.st-no-results, .st-default-no-results-block',
    searchOos: '.st-oos-badge, .st-oos-btn, .badge--sold-out',
    searchCount: '#ProductCount, .st-results-count, .st-product-count',
    filterValue: '.st-filter-value, .st-mobile-filter-values-list-item, .facets__label input, .mobile-facets__label input',
    filterGroup: '.st-single-filter-body, .st-filter-list, .facets__disclosure, .mobile-facets__details',
    filterTitle: '.st-filter-title, .st-filter-heading, .facets__summary, .mobile-facets__summary',
    sortControl: '.st-sort-click, .st-btn.st-sort-btn, #SortBy, .facet-filters__sort',

    // Cart / checkout (Dawn cart page + GoKwik drawer)
    checkoutButton: '.gokwik-checkout, #gokwik-checkout, [class*="gokwik-button"] button, button[onclick*="onCheckoutClick"], .cart__checkout-button, [name="checkout"], button[data-gokwik-checkout]',
    couponInput: 'input[name*="coupon"], input[id*="coupon"], input[placeholder*="coupon" i], input[placeholder*="discount" i], input[name*="discount"]',
    wishlistIcon: '.wishlist-icon-button, [class*="wishlist"] a, a[href*="wishlist"]',

    // Blog
    articleBody: '.article-template, .article, .blog-article, .rte',
    contentCta: '.article-template a.button, .article-template .btn, .article-template a[class*="cta"], .rte a.button'
  };

  /* ======================================================================
   * TRANSPORT
   * ==================================================================== */
  function ensureCt() {
    window.clevertap = window.clevertap || { event: [], profile: [], account: [], onUserLogin: [], notifications: [], privacy: [] };
    return window.clevertap;
  }

  function push(name, props) {
    var clean = {};
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      var v = props[k];
      if (v === undefined || v === null || v === '' || (typeof v === 'number' && isNaN(v))) continue;
      clean[k] = typeof v === 'string' ? v.slice(0, 500) : v;
    }
    clean['CT Source'] = 'BeastLife Theme';
    clean['Page Type'] = pageType();
    try { ensureCt().event.push(name, clean); } catch (e) { if (DEBUG) console.error('[BL CT]', e); }
    if (DEBUG) console.log('[BL CT] ' + name, clean);
  }

  function identify(phone, extra) {
    if (!CONFIG.identifyOnKwikPassLogin) return;
    var site = {};
    var p = String(phone || '').replace(/[^0-9]/g, '');
    if (p.length >= 10) site.Phone = CONFIG.phoneCountryCode + p.slice(-10);
    for (var k in extra) if (extra[k]) site[k] = extra[k];
    if (!Object.keys(site).length) return;
    try { ensureCt().onUserLogin.push({ Site: site }); } catch (e) {}
    if (DEBUG) console.log('[BL CT] onUserLogin', site);
  }

  /* ======================================================================
   * HELPERS
   * ==================================================================== */
  function txt(el, max) {
    if (!el) return '';
    var t = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.slice(0, max || 200);
  }
  function altOf(el) {
    var img = el && (el.tagName === 'IMG' ? el : el.querySelector('img'));
    return img ? (img.getAttribute('alt') || '').trim() : '';
  }
  function money(s) {
    var n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? undefined : n;
  }
  function paise(n) { return typeof n === 'number' ? Math.round(n) / 100 : undefined; }
  function indexIn(list, el) {
    var i = Array.prototype.indexOf.call(list, el);
    return i < 0 ? undefined : i + 1;
  }
  function abs(href) {
    try { return new URL(href, location.href).href; } catch (e) { return href || ''; }
  }
  function handleOf(href) { return ((href || '').match(/\/products\/([^/?#]+)/) || [])[1] || ''; }
  function storage(k) { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } }
  function session(k, v) {
    try { if (v !== undefined) sessionStorage.setItem(k, v); return sessionStorage.getItem(k) || ''; } catch (e) { return ''; }
  }
  function query(k) { return new URLSearchParams(location.search).get(k) || ''; }
  function debounce(fn, ms) { var t; return function () { var a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(null, a); }, ms); }; }
  function once(key) { if (once.seen[key]) return false; once.seen[key] = true; return true; }
  once.seen = {};

  function pageType() {
    var st = window.__st || {};
    if (st.p) return st.p;
    var p = location.pathname;
    if (p === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(p)) return 'index';
    if (/\/products\//.test(p)) return 'product';
    if (/\/collections\//.test(p)) return 'collection';
    if (/\/search/.test(p)) return 'search';
    if (/\/cart/.test(p)) return 'cart';
    if (/\/blogs\/[^/]+\/[^/]+/.test(p)) return 'article';
    if (/\/blogs\//.test(p)) return 'blog';
    if (/\/pages\//.test(p)) return 'page';
    return 'other';
  }
  function screenName() {
    var t = pageType();
    return { index: 'Home', product: 'PDP', collection: 'PLP', search: 'Search', cart: 'Cart', article: 'Blog Article', blog: 'Blog', page: 'Page:' + location.pathname.split('/').pop() }[t] || t;
  }
  function previousScreen() {
    var s = session('bl_ct_prev_screen');
    if (s) return s;
    try { var r = new URL(document.referrer); if (r.origin === location.origin) return r.pathname; return r.hostname; } catch (e) { return ''; }
  }
  function sessionId() {
    var s = session('bl_ct_session');
    if (!s) { s = Date.now().toString(36) + Math.random().toString(36).slice(2, 8); session('bl_ct_session', s); }
    return s;
  }

  // "Performance Fermented Yeast Protein | Chocolate | 2 kg" → { name, flavour, packSize }
  function parseTitle(title) {
    var parts = String(title || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    var out = { name: parts[0] || title || '', flavour: '', packSize: '' };
    for (var i = 1; i < parts.length; i++) {
      if (/^\d+(\.\d+)?\s*(g|kg|gm|ml|l|caps?|tabs?|pcs|serv|scoops?)\b/i.test(parts[i])) out.packSize = parts[i];
      else if (!out.flavour) out.flavour = parts[i];
    }
    return out;
  }
  function categoryFrom(type, tags) {
    if (type) return type;
    var t = (tags || []).map(String);
    for (var i = 0; i < CONFIG.categoryTags.length; i++) {
      var c = CONFIG.categoryTags[i].toLowerCase();
      for (var j = 0; j < t.length; j++) if (t[j].toLowerCase() === c) return CONFIG.categoryTags[i];
    }
    return '';
  }

  /* ======================================================================
   * PRODUCT CONTEXT (PDP)
   * ==================================================================== */
  var product = null;

  // The CleverTap app inlines the full product JSON as `const __wzrk_product_json`.
  // Read the binding if visible, else scan the inline script text for it.
  function readInlineProductJson() {
    try { if (typeof __wzrk_product_json !== 'undefined' && __wzrk_product_json) return __wzrk_product_json; } catch (e) {}
    var scripts = document.querySelectorAll('script:not([src])');
    for (var s = 0; s < scripts.length; s++) {
      var t = scripts[s].textContent || '';
      var i = t.indexOf('__wzrk_product_json');
      if (i < 0) continue;
      var start = t.indexOf('{', i);
      if (start < 0) continue;
      var depth = 0, inStr = false, esc = false, q = '';
      for (var j = start; j < t.length; j++) {
        var ch = t[j];
        if (inStr) { if (esc) esc = false; else if (ch === '\\') esc = true; else if (ch === q) inStr = false; continue; }
        if (ch === '"' || ch === "'") { inStr = true; q = ch; continue; }
        if (ch === '{') depth++;
        else if (ch === '}') { depth--; if (depth === 0) { try { return JSON.parse(t.slice(start, j + 1)); } catch (e) { return null; } } }
      }
    }
    return null;
  }

  function loadProduct(json) {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (!meta || !meta.product) return null;
    json = json || readInlineProductJson();
    var p = json || meta.product;
    var variants = p.variants || [];
    var selectedId = meta.selectedVariantId || query('variant');
    var v = null;
    for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(selectedId)) v = variants[i];
    v = v || variants[0] || {};
    var parsed = parseTitle(p.title || v.name || '');
    var price = paise(v.price);
    var mrp = paise(v.compare_at_price || p.compare_at_price) || price;
    return {
      id: String(p.id || ''),
      handle: p.handle || meta.product.handle || '',
      sku: v.sku || '',
      name: parsed.name,
      title: p.title || v.name || '',
      flavour: parsed.flavour,
      packSize: parsed.packSize,
      variantId: String(v.id || ''),
      variantTitle: v.title && v.title !== 'Default Title' ? v.title : (v.public_title || ''),
      price: price,
      mrp: mrp,
      discount: price !== undefined && mrp !== undefined ? Math.max(0, +(mrp - price).toFixed(2)) : undefined,
      available: typeof v.available === 'boolean' ? v.available : (p.available !== false),
      category: categoryFrom(p.type, p.tags),
      tags: p.tags || [],
      fromJson: !!json
    };
  }
  // Ensure the rich JSON (MRP, tags, availability) is present before PDP events fire.
  function withProductJson(cb) {
    if (!product || product.fromJson || !window.fetch) return cb();
    fetch('/products/' + product.handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (json) { product = loadProduct(json) || product; })
      .catch(function () {})
      .then(cb);
  }
  function productProps(p) {
    p = p || product;
    if (!p) return {};
    return { 'Product ID': p.id, 'SKU': p.sku, 'Product Name': p.name, 'Category': p.category, 'Variant': p.variantTitle || p.variantId, 'Flavour': p.flavour, 'Pack Size': p.packSize };
  }

  // Sibling products behind flavour / weight swatches (each is its own product)
  var siblings = {};
  function prefetchSiblings() {
    var links = document.querySelectorAll(SEL.flavourSwatch + ', ' + SEL.weightSwatch);
    var run = function () {
      Array.prototype.forEach.call(links, function (a) {
        var h = handleOf(a.getAttribute('href'));
        if (!h || siblings[h]) return;
        siblings[h] = { pending: true };
        fetch('/products/' + h + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (p) {
          var v = (p.variants || [])[0] || {};
          var parsed = parseTitle(p.title);
          siblings[h] = { id: String(p.id), sku: v.sku || '', variantId: String(v.id || ''), price: paise(v.price), flavour: parsed.flavour, packSize: parsed.packSize, available: p.available };
        }).catch(function () { siblings[h] = {}; });
      });
    };
    if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 1500);
  }

  /* ======================================================================
   * CART ENGINE — snapshot, mutation interception, diff → events
   * ==================================================================== */
  var cart = null;                 // last /cart.js snapshot
  var lastCartAction = { type: '', at: 0, component: '', placement: '', position: undefined };
  function cartProps(c) {
    c = c || cart;
    if (!c) return {};
    var qty = 0; (c.items || []).forEach(function (i) { qty += i.quantity || 0; });
    var codes = (c.discount_codes || []).filter(function (d) { return d.applicable !== false; }).map(function (d) { return d.code; });
    return { 'Cart ID': c.token, 'Item Count': c.item_count, 'Total Quantity': qty, 'Cart Value': paise(c.total_price), 'Discount': paise(c.total_discount), 'Coupon Code': codes.join(',') || undefined };
  }
  function lineKey(i) { return i.key || (i.variant_id + ':' + JSON.stringify(i.properties || {})); }
  function lineProps(i) {
    var parsed = parseTitle(i.product_title || i.title);
    return {
      'Product ID': String(i.product_id), 'SKU': i.sku || '', 'Variant': i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : String(i.variant_id),
      'Flavour': parsed.flavour, 'Pack Size': parsed.packSize, 'Product Name': parsed.name,
      'Unit Price': paise(i.final_price !== undefined ? i.final_price : i.price), 'Price': paise(i.final_price !== undefined ? i.final_price : i.price),
      'Discount': paise(i.total_discount) || 0, 'Category': categoryFrom(i.product_type, [])
    };
  }
  function isGift(i) { return !!(i.properties && ('_Gifted' in i.properties || /gift|freebie/i.test(Object.keys(i.properties).join(' ')))); }
  function isGokwikUpsell(i) { return !!(i.properties && Object.keys(i.properties).some(function (k) { return /_gokwik_upsell/i.test(k); })); }
  function bundleIdOf(i) {
    if (!i.properties) return '';
    for (var k in i.properties) if (/bundle/i.test(k) && i.properties[k]) return String(i.properties[k]);
    return '';
  }

  function diffCart(prev, next, source) {
    var before = {}, after = {};
    (prev && prev.items || []).forEach(function (i) { before[lineKey(i)] = i; });
    (next.items || []).forEach(function (i) { after[lineKey(i)] = i; });
    var cp = cartProps(next);
    var component = (Date.now() - lastCartAction.at < 4000) ? lastCartAction.component : 'auto';
    var k;

    for (k in after) {
      var n = after[k], b = before[k];
      var delta = n.quantity - (b ? b.quantity : 0);
      if (delta <= 0) continue;
      if (isGift(n)) {
        push('Free Gift Added', { 'Gift ID': String(n.variant_id), 'Gift Name': n.product_title, 'Cart ID': cp['Cart ID'], 'Cart Value': cp['Cart Value'] });
        push('Free Gift Unlocked', { 'Gift ID': String(n.variant_id), 'Gift Name': n.product_title, 'Cart Value': cp['Cart Value'] });
        continue;
      }
      if (!b || source === 'add') {
        if (CONFIG.pluginOverlap !== 'plugin') {
          var lp = lineProps(n);
          push('Added to Cart', {
            'Product ID': lp['Product ID'], 'SKU': lp.SKU, 'Variant': lp.Variant, 'Flavour': lp.Flavour, 'Pack Size': lp['Pack Size'],
            'Quantity': delta, 'Unit Price': lp['Unit Price'], 'Discount': lp.Discount,
            'Cart ID': cp['Cart ID'], 'Cart Value': cp['Cart Value'], 'Source Screen': screenName(), 'Source Component': component
          });
        }
        if (isGokwikUpsell(n)) push('Upsell Added', { 'Source Product ID': product ? product.id : '', 'Upsell Product ID': String(n.product_id) });
        if (component === 'recommendation') {
          push('Recommendation Added to Cart', { 'Recommendation Type': 'cross-sell', 'Recommended Product ID': String(n.product_id), 'Source Product ID': product ? product.id : '' });
          push('Cross Sell Added', { 'Source Product ID': product ? product.id : '', 'Recommended Product ID': String(n.product_id), 'Placement': 'PDP' });
        }
        var bid = bundleIdOf(n) || (bundle.active ? bundle.id : '');
        if (bid) push('Bundle Added to Cart', { 'Bundle ID': bid, 'Cart ID': cp['Cart ID'], 'Bundle Value': paise(n.final_line_price !== undefined ? n.final_line_price : n.line_price) });
      } else {
        push('Cart Item Quantity Changed', { 'Cart ID': cp['Cart ID'], 'Product ID': String(n.product_id), 'Old Quantity': b.quantity, 'New Quantity': n.quantity });
        push('Quantity Changed', { 'Product ID': String(n.product_id), 'Old Quantity': b.quantity, 'New Quantity': n.quantity, 'Price': paise(n.final_price !== undefined ? n.final_price : n.price) });
      }
    }
    for (k in before) {
      var o = before[k], a = after[k];
      if (a && a.quantity >= o.quantity) continue;
      if (isGift(o) && !a) { push('Free Gift Removed', { 'Gift ID': String(o.variant_id), 'Cart ID': cp['Cart ID'] }); continue; }
      if (!a) {
        push('Cart Item Removed', { 'Cart ID': cp['Cart ID'], 'Product ID': String(o.product_id), 'Quantity': o.quantity });
      } else {
        push('Cart Item Quantity Changed', { 'Cart ID': cp['Cart ID'], 'Product ID': String(o.product_id), 'Old Quantity': o.quantity, 'New Quantity': a.quantity });
        push('Quantity Changed', { 'Product ID': String(o.product_id), 'Old Quantity': o.quantity, 'New Quantity': a.quantity, 'Price': paise(a.final_price !== undefined ? a.final_price : a.price) });
      }
    }
  }

  var syncing = false, syncQueued = null;
  function syncCart(source, silent) {
    if (!window.fetch) return;
    if (syncing) { syncQueued = source; return; }
    syncing = true;
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (c) {
      if (c && Array.isArray(c.items)) {
        if (cart && !silent) diffCart(cart, c, source);
        cart = c;
      }
    }).catch(function () {}).then(function () {
      syncing = false;
      if (syncQueued) { var s = syncQueued; syncQueued = null; syncCart(s); }
    });
  }
  var syncCartSoon = debounce(function (src) { syncCart(src); }, 350);

  function cartSourceFor(url) {
    var m = String(url).match(/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/);
    return m ? m[1] : '';
  }

  function interceptCartRequests() {
    // fetch
    if (window.fetch) {
      var _fetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var src = cartSourceFor(url);
        var gk = CONFIG.mirrorGokwikCartEvents && /hits\.gokwik\.co\/api\/v1\/events/.test(url);
        if (gk) { try { mirrorGokwik(init && init.body); } catch (e) {} }
        var p = _fetch.apply(this, arguments);
        if (src) p.then(function (res) { if (res && res.ok) syncCartSoon(src); }).catch(function () {});
        return p;
      };
    }
    // XHR
    if (window.XMLHttpRequest) {
      var _open = XMLHttpRequest.prototype.open, _send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) { this.__blUrl = url; return _open.apply(this, arguments); };
      XMLHttpRequest.prototype.send = function (body) {
        var src = cartSourceFor(this.__blUrl || '');
        if (CONFIG.mirrorGokwikCartEvents && /hits\.gokwik\.co\/api\/v1\/events/.test(this.__blUrl || '')) { try { mirrorGokwik(body); } catch (e) {} }
        if (src) this.addEventListener('load', function () { if (this.status >= 200 && this.status < 300) syncCartSoon(src); });
        return _send.apply(this, arguments);
      };
    }
    // sendBeacon (GoKwik hits sometimes go this way)
    if (navigator.sendBeacon && CONFIG.mirrorGokwikCartEvents) {
      var _beacon = navigator.sendBeacon.bind(navigator);
      navigator.sendBeacon = function (url, data) {
        if (/hits\.gokwik\.co\/api\/v1\/events/.test(String(url))) { try { mirrorGokwik(data); } catch (e) {} }
        return _beacon(url, data);
      };
    }
    // KwikCart announces count changes; Dawn themes fire cart:refresh
    document.addEventListener('cartcount:update', function () { syncCartSoon('event'); });
    window.addEventListener('cartcount:update', function () { syncCartSoon('event'); });
    document.addEventListener('cart:refresh', function () { syncCartSoon('event'); });
    // Full-page ATC form posts (no JS) land on /cart — snapshot on load handles it.
  }

  /* ======================================================================
   * GOKWIK CART DRAWER MIRROR  (hits.gokwik.co analytics → sheet events)
   * ==================================================================== */
  var GK_MAP = {
    'checkout_button_clicked_sc': 'Checkout Button Clicked',
    'coupon_applied': 'Coupon Applied',
    'coupon_code_applied_sc': 'Coupon Applied',
    'upsell_shown_sc': 'Upsell Viewed',
    'one_tick_upsell_shown': 'Upsell Viewed',
    'sku_one_tick_upsell_shown': 'Upsell Viewed',
    'upsell_list_product_clicked': 'Upsell Clicked',
    'one_tick_upsell_clicked': 'Upsell Clicked',
    'sku_one_tick_upsell_clicked': 'Upsell Clicked',
    'upsell_added_sc': 'Upsell Added',
    'sku_one_tick_upsell_added': 'Upsell Added',
    'freebie_added_sc': 'Free Gift Added',
    'tier_gift_selected_sc': 'Free Gift Added',
    'freebie_removed_sc': 'Free Gift Removed'
  };
  var gkSeen = {};
  var lastCheckoutClick = 0;
  function mirrorGokwik(body) {
    if (!body) return;
    var data = body;
    if (typeof body === 'string') { try { data = JSON.parse(body); } catch (e) { return; } }
    if (data instanceof Blob) { data.text().then(function (t) { mirrorGokwik(t); }); return; }
    var events = Array.isArray(data) ? data : (Array.isArray(data.events) ? data.events : [data]);
    events.forEach(function (ev) {
      if (!ev || typeof ev !== 'object') return;
      var name = ev.event || ev.event_name || ev.eventName || ev.name || ev.action || '';
      if (DEBUG) console.log('[BL CT] gokwik hit', name, ev);
      var mapped = GK_MAP[name];
      if (!mapped) return;
      var key = mapped + ':' + Math.floor(Date.now() / 1500);
      if (gkSeen[key]) return; gkSeen[key] = true;
      var cp = cartProps();
      var props = { 'GoKwik Event': name, 'Placement': 'Cart Drawer' };
      if (/Checkout/.test(mapped)) {
        if (Date.now() - lastCheckoutClick < 1500) return;
        lastCheckoutClick = Date.now();
        props['Cart ID'] = cp['Cart ID']; props['Cart Value'] = cp['Cart Value']; props['Item Count'] = cp['Item Count'];
      }
      if (/Coupon/.test(mapped)) { props['Coupon Code'] = ev.coupon || ev.coupon_code || ev.code || ev.label || ''; props['Cart Value'] = cp['Cart Value']; props['Discount Amount'] = money(ev.discount || ev.discount_amount); }
      if (/Upsell/.test(mapped)) { props['Source Product ID'] = product ? product.id : ''; props['Upsell Product ID'] = String(ev.product_id || ev.variant_id || ev.upsell_variant_id || ''); }
      if (/Gift/.test(mapped)) { props['Gift ID'] = String(ev.variant_id || ev.product_id || ''); props['Cart ID'] = cp['Cart ID']; props['Cart Value'] = cp['Cart Value']; }
      push(mapped, props);
    });
  }

  // Wrap globals KwikCart / GoKwik define later, whenever they get assigned.
  function wrapGlobal(name, before) {
    var current = window[name];
    try {
      Object.defineProperty(window, name, {
        configurable: true, enumerable: true,
        get: function () { return current; },
        set: function (fn) {
          current = typeof fn === 'function' ? function () { try { before.apply(null, arguments); } catch (e) {} return fn.apply(this, arguments); } : fn;
        }
      });
      if (typeof current === 'function') window[name] = current; // re-assign to wrap an already-defined fn
    } catch (e) {}
  }

  /* ======================================================================
   * VIEW EVENTS (IntersectionObserver, once per pageview)
   * ==================================================================== */
  var io = null;
  function observe(el, name, propsFn, key) {
    if (!el || !('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var tall = entry.intersectionRect.height >= window.innerHeight * CONFIG.viewThreshold;
          if (entry.intersectionRatio < CONFIG.viewThreshold && !tall) return;
          var spec = entry.target.__blCtView; if (!spec) return;
          io.unobserve(entry.target);
          if (once('view:' + spec.key)) push(spec.name, spec.propsFn(entry.target));
        });
      }, { threshold: [0, 0.25, 0.5, 0.75] });
    }
    el.__blCtView = { name: name, propsFn: propsFn, key: key || name };
    io.observe(el);
  }
  function observeAll(selector, name, propsFn, keyPrefix) {
    Array.prototype.forEach.call(document.querySelectorAll(selector), function (el, i) {
      observe(el, name, propsFn, (keyPrefix || name) + ':' + i);
    });
  }

  /* ======================================================================
   * PAGE-LEVEL EVENTS
   * ==================================================================== */
  function trackPageViews() {
    var t = pageType();
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var page = meta.page || {};

    if (t === 'index') {
      push('Home Page Viewed', { 'Session ID': sessionId(), 'Source': query('utm_source') || previousScreen() || 'direct', 'Campaign': query('utm_campaign') });
    }
    if (t === 'collection') {
      var h1 = document.querySelector('.collection-hero__title, h1');
      var handle = (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || '';
      var count = money(txt(document.querySelector(SEL.searchCount))) || document.querySelectorAll(SEL.productCard).length;
      push('Category Viewed', { 'Category ID': String(page.resourceId || handle), 'Category Name': txt(h1, 80) || handle, 'Source Screen': previousScreen() });
      push('Product Listing Viewed', { 'Category ID': String(page.resourceId || handle), 'Product Count': count, 'Source Screen': previousScreen() });
    }
    if (t === 'article') {
      var blog = (location.pathname.match(/\/blogs\/([^/]+)/) || [])[1] || '';
      push('Blog Viewed', { 'Content ID': String(page.resourceId || location.pathname), 'Category': blog });
    }
    if (t === 'page' && /wishlist/i.test(location.pathname)) {
      push('Wishlist Viewed', { 'Item Count': document.querySelectorAll('.wishlist-item, [class*="wishlist-product"], ' + SEL.productCard).length });
    }
    if (t === 'page' && /lab-report/i.test(location.pathname)) {
      push('Lab Report Viewed', { 'Product ID': query('product') || '', 'Report ID': location.pathname, 'Report Type': 'Lab Reports Page' });
      push('Trust Content Viewed', { 'Content Type': 'Lab report', 'Content ID': location.pathname, 'Placement': 'Lab Reports Page' });
    }
    if (t === 'page' && /authentication/i.test(location.pathname)) {
      push('Trust Content Viewed', { 'Content Type': 'Authenticity', 'Content ID': location.pathname, 'Placement': 'Authentication Page' });
    }
    if (t === 'cart') cartViewed('Cart Page');
    if (query('openCart') === 'true') setTimeout(function () { cartViewed('Cart Drawer'); }, 1200);
    if (t === 'search') trackSearchResults('page');
  }

  function cartViewed(placement) {
    var fire = function () {
      var cp = cartProps();
      push('Cart Viewed', { 'Cart ID': cp['Cart ID'], 'Item Count': cp['Item Count'], 'Total Quantity': cp['Total Quantity'], 'Cart Value': cp['Cart Value'], 'Discount': cp.Discount, 'Coupon Code': cp['Coupon Code'], 'Placement': placement });
    };
    if (cart) fire(); else setTimeout(fire, 800);
  }

  /* ======================================================================
   * PDP EVENTS
   * ==================================================================== */
  var bundle = { active: false, id: '', offerId: '' };
  function trackPdp() {
    if (!product) return;
    var placement = session('bl_ct_placement');
    session('bl_ct_placement', '');

    if (CONFIG.pluginOverlap !== 'plugin') {
      var pp = productProps();
      push('Product Viewed', {
        'Product ID': pp['Product ID'], 'SKU': pp.SKU, 'Product Name': pp['Product Name'], 'Category': pp.Category, 'Variant': pp.Variant,
        'Flavour': pp.Flavour, 'Pack Size': pp['Pack Size'], 'Selling Price': product.price, 'MRP': product.mrp, 'Discount': product.discount,
        'Source Screen': previousScreen(), 'Placement': placement || undefined
      });
    }
    if (product.available === false) {
      var pp2 = productProps();
      push('Product Out of Stock Viewed', { 'Product ID': pp2['Product ID'], 'SKU': pp2.SKU, 'Variant': pp2.Variant, 'Flavour': pp2.Flavour, 'Pack Size': pp2['Pack Size'], 'Source Screen': previousScreen() });
    }

    // Bundle PDP? (EasyGift attaches bundles to parent products)
    try {
      var egMeta = document.getElementById('easygift-shop');
      var eg = egMeta && JSON.parse(egMeta.getAttribute('content') || '{}');
      var list = (eg && (eg.bundleLinkData || (eg.settings && eg.settings.bundleLinkData))) || [];
      list.forEach(function (b) {
        var h = b.parentProductShopifyData && b.parentProductShopifyData.handle;
        if (h && h === product.handle) { bundle = { active: true, id: String(b.bundleId || b._id), offerId: String(b._id || ''), name: b.bundleName || '' }; }
      });
    } catch (e) {}
    if (bundle.active) {
      push('Bundle Started', { 'Bundle ID': bundle.id, 'Offer ID': bundle.offerId, 'Required Items': (String(bundle.name).match(/\b(\d+)\b/) || [])[1] ? parseInt((String(bundle.name).match(/\b(\d+)\b/) || [])[1], 10) : undefined });
      push('Offer Viewed', { 'Offer ID': bundle.offerId, 'Offer Name': bundle.name, 'Offer Type': 'Bundle', 'Placement': 'PDP' });
    }

    // Weight options viewed → part of trust/PDP context; Variant selection handled in click router
    observe(document.querySelector(SEL.keyBenefits), 'Benefits Viewed', function () { return { 'Product ID': product.id }; });
    observe(document.querySelector(SEL.reviewsWidget), 'Reviews Viewed', function (el) {
      var d = el.querySelector(SEL.reviewsData) || document.querySelector(SEL.reviewsData);
      return { 'Product ID': product.id, 'Review Count': d ? parseInt(d.getAttribute('data-number-of-reviews'), 10) : undefined };
    });
    observe(document.querySelector(SEL.pdpCerts), 'Trust Content Viewed', function () { return { 'Content Type': 'Quality', 'Content ID': 'pdp-certifications', 'Product ID': product.id, 'Placement': 'PDP' }; }, 'pdp-certs');

    // Related products = Cross Sell / Recommendation Viewed (one event per list, ids comma-joined)
    var related = document.querySelector(SEL.relatedSection);
    if (related) {
      var whenRendered = function (tries) {
        var cards = related.querySelectorAll(SEL.productCard);
        if (!cards.length && tries < 20) return setTimeout(function () { whenRendered(tries + 1); }, 500);
        if (!cards.length) return;
        observe(related, 'Cross Sell Viewed', function () {
          var ids = Array.prototype.map.call(cards, cardProductId).filter(Boolean).join(',');
          push('Recommendation Viewed', { 'Recommendation Type': 'cross-sell', 'Recommended Product ID': ids, 'Source Product ID': product.id });
          return { 'Source Product ID': product.id, 'Recommended Product ID': ids, 'Placement': 'PDP', 'Position': 1 };
        });
      };
      whenRendered(0);
    }

    // Quantity input
    var q = document.querySelector(SEL.quantityInput);
    if (q) {
      q.__blLast = parseInt(q.value, 10) || 1;
      q.addEventListener('change', function () {
        var nv = parseInt(q.value, 10) || 1;
        if (nv === q.__blLast) return;
        push('Quantity Changed', { 'Product ID': product.id, 'Old Quantity': q.__blLast, 'New Quantity': nv, 'Price': product.price });
        q.__blLast = nv;
      });
    }

    // Gallery navigation → Product Image Viewed
    Array.prototype.forEach.call(document.querySelectorAll(SEL.mediaGallery + ' slider-component'), function (sc) {
      sc.addEventListener('slideChanged', function (e) {
        var n = e.detail && e.detail.currentPage;
        if (n) push('Product Image Viewed', { 'Product ID': product.id, 'Image Number': n });
      });
    });

    // Pincode
    watchPincode();
    prefetchSiblings();
  }

  var pendingPincode = '', pincodeArmed = false;
  function watchPincode() {
    var result = document.querySelector(SEL.pincodeResult);
    if (!result || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (!pincodeArmed) return;
      var t = txt(result, 160);
      if (!t || /checking/i.test(t)) return;
      pincodeArmed = false;
      var serviceable = /expected delivery/i.test(t);
      var days;
      var d = t.match(/(\d{4}-\d{2}-\d{2}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}|\d{2}[-/]\d{2}[-/]\d{4})/);
      if (d) { var dt = new Date(d[1]); if (!isNaN(dt)) days = Math.max(0, Math.round((dt - Date.now()) / 864e5)); }
      var input = document.querySelector(SEL.pincodeInput);
      push('Pincode Checked', { 'Pincode': pendingPincode || (input && input.value) || '', 'Serviceable': serviceable, 'Delivery Days': days, 'Product ID': product ? product.id : '' });
    }).observe(result, { childList: true, subtree: true, characterData: true });
  }

  /* ======================================================================
   * CARDS / CATEGORIES / OFFERS / TRUST — click router
   * ==================================================================== */
  function cardProductId(card) {
    var link = card.querySelector('a[id^="CardLink-"]');
    var fromId = link && link.id ? (link.id.match(/-(\d+)$/) || [])[1] : '';
    return card.getAttribute('data-product-id') || fromId || handleOf((card.querySelector('a[href*="/products/"]') || {}).getAttribute ? card.querySelector('a[href*="/products/"]').getAttribute('href') : '');
  }
  function cardPlacement(card) {
    if (card.closest(SEL.relatedSection)) return 'Recommendation';
    if (card.closest('.st-results-list, .template-search__results, .st-autocomplete-content')) return 'Search';
    if (card.closest('[class*="gokwik"], [class*="kwik-cart"]')) return 'Cart';
    var t = pageType();
    if (t === 'index') return 'Home';
    if (t === 'collection') return 'PLP';
    if (t === 'search') return 'Search';
    return screenName();
  }
  function cardPosition(card) {
    var li = card.closest(SEL.cardListItem);
    return li && li.parentElement ? indexIn(li.parentElement.children, li) : undefined;
  }
  function cardInfo(card) {
    var link = card.querySelector(SEL.cardTitleLink);
    var price = money(txt(card.querySelector(SEL.cardPriceSale))) || money(txt(card.querySelector(SEL.cardPriceRegular)));
    var parsed = parseTitle(txt(link, 200) || altOf(card));
    return { id: cardProductId(card), sku: card.getAttribute('data-sku') || '', name: parsed.name, flavour: parsed.flavour, packSize: parsed.packSize, price: price, category: card.getAttribute('data-product-type') || '', placement: cardPlacement(card), position: cardPosition(card), href: link ? link.getAttribute('href') : '' };
  }

  function onClick(e) {
    var target = e.target && e.target.nodeType === 3 ? e.target.parentElement : e.target;
    if (!target || !target.closest) return;
    var el;

    // --- Checkout / Buy Now ---
    if ((el = target.closest(SEL.checkoutButton))) {
      if (Date.now() - lastCheckoutClick > 1500) {
        lastCheckoutClick = Date.now();
        var cp = cartProps();
        push('Checkout Button Clicked', { 'Cart ID': cp['Cart ID'], 'Cart Value': cp['Cart Value'], 'Item Count': cp['Item Count'] });
      }
      return;
    }
    if (product && (el = target.closest(SEL.buyNow))) {
      var qb = document.querySelector(SEL.quantityInput);
      push('Buy Now Clicked', { 'Product ID': product.id, 'SKU': product.sku, 'Variant': product.variantTitle || product.variantId, 'Quantity': qb ? parseInt(qb.value, 10) || 1 : 1, 'Price': product.price, 'Source Screen': screenName() });
      return;
    }

    // --- Add to cart intent (records component for the cart diff) ---
    var atc = target.closest(SEL.cardAtc + ', ' + SEL.pdpAtc + ', ' + SEL.stickyAtc);
    if (atc) {
      var card = atc.closest(SEL.productCard);
      var component = card ? (cardPlacement(card) === 'Recommendation' ? 'recommendation' : (card.matches('.st-product-card') ? 'searchtap_card' : 'quick_add'))
        : atc.closest('[id*="sticky_atc"]') ? 'sticky_atc' : 'pdp_form';
      lastCartAction = { type: 'add', at: Date.now(), component: component };
      if (card) {
        var ci = cardInfo(card);
        var qty = parseInt((card.querySelector('input[name="quantity"]') || {}).value, 10) || 1;
        push('Product Card Add to Cart', { 'Product ID': ci.id, 'SKU': ci.sku, 'Quantity': qty, 'Price': ci.price, 'Placement': ci.placement, 'Position': ci.position });
      }
      return;
    }

    // --- Product cards ---
    var pcard = target.closest(SEL.productCard);
    if (pcard && pcard.querySelector('a[href*="/products/"]') && !target.closest('button, form, [class*="wishlist"]')) {
      var info = cardInfo(pcard);
      session('bl_ct_placement', info.placement);
      session('bl_ct_prev_screen', screenName());
      push('Product Card Clicked', { 'Product ID': info.id, 'SKU': info.sku, 'Category': info.category, 'Placement': info.placement, 'Position': info.position });
      if (info.placement === 'Recommendation' && product) {
        push('Recommendation Clicked', { 'Recommendation Type': 'cross-sell', 'Recommended Product ID': info.id, 'Source Product ID': product.id, 'Position': info.position });
        push('Cross Sell Clicked', { 'Source Product ID': product.id, 'Recommended Product ID': info.id, 'Placement': 'PDP', 'Position': info.position });
      }
      if (info.placement === 'Search') {
        push('Search Result Product Clicked', { 'Search Term': currentSearchTerm(), 'Product ID': info.id, 'Position': info.position, 'Filters Used': activeFilters() });
      }
      return;
    }

    // --- Categories ---
    if ((el = target.closest(SEL.category)) && !el.closest(SEL.footer)) {
      var href = el.getAttribute('href') || '';
      if (/^javascript:|^#/.test(href) && !el.matches('.fc-tab-btn')) return;
      var chandle = (href.match(/\/collections\/([^/?#]+)/) || [])[1] || '';
      var placement = el.closest('.bl-cat-section') ? 'Home Category Row' : el.closest('.section-shop-by-flavors') ? 'Home Flavours' : el.matches('.fc-tab-btn') ? 'Home Bestseller Tabs' : el.closest('.menu-drawer, .custom-mobile-drawer') ? 'Mobile Menu' : el.closest(SEL.header) ? 'Header Menu' : 'Page';
      var citem = el.closest('li, .swatch-item, .bl-cat-item, .sbf__card') || el;
      var cname = txt(el, 60) || altOf(el);
      push('Category Clicked', { 'Category ID': chandle || href.replace(/^\/+/, '') || cname.toLowerCase().replace(/[^a-z0-9]+/g, '-'), 'Category Name': cname, 'Placement': placement, 'Position': citem.parentElement ? indexIn(citem.parentElement.children, citem) : undefined });
      session('bl_ct_prev_screen', screenName());
      return;
    }

    // --- Offers: hero banners, ticker, promo blocks ---
    if ((el = target.closest(SEL.banner))) {
      push('Offer Banner Clicked', { 'Offer ID': el.getAttribute('data-banner-id') || el.getAttribute('data-banner-name') || '', 'Offer Name': el.getAttribute('data-banner-name') || altOf(el), 'Placement': 'Home Hero', 'Position': parseInt(el.getAttribute('data-banner-index'), 10) || undefined });
      push('Offer Clicked', { 'Offer ID': el.getAttribute('data-banner-id') || el.getAttribute('data-banner-name') || '', 'Offer Name': el.getAttribute('data-banner-name') || altOf(el), 'Placement': 'Home Hero', 'Position': parseInt(el.getAttribute('data-banner-index'), 10) || undefined });
      return;
    }
    if ((el = target.closest(SEL.ticker))) {
      push('Offer Clicked', { 'Offer ID': 'ticker', 'Offer Name': txt(el, 80), 'Placement': 'Home Ticker', 'Position': indexIn(el.parentElement.querySelectorAll(SEL.ticker), el) });
      return;
    }

    // --- Trust: lab report ---
    if ((el = target.closest(SEL.labReportLink))) {
      push('Lab Report Clicked', { 'Product ID': product ? product.id : '', 'Report ID': el.getAttribute('href') || '', 'Placement': el.closest(SEL.footer) ? 'Footer' : screenName() });
      return;
    }

    // --- Search filters / sort ---
    if ((el = target.closest(SEL.filterValue))) {
      var group = el.closest(SEL.filterGroup);
      var ftitle = group ? txt(group.querySelector(SEL.filterTitle), 40) : '';
      var fval = txt(el, 60) || (el.value || '');
      setTimeout(function () { push('Filter Applied', { 'Filter Type': ftitle || 'Filter', 'Filter Value': fval, 'Category': currentCategory() }); }, 0);
      return;
    }
    if ((el = target.closest(SEL.sortControl)) && !el.matches('select')) {
      push('Sort Applied', { 'Sort Type': txt(el, 40), 'Category': currentCategory() });
      return;
    }

    // --- PDP ---
    if (product) {
      if ((el = target.closest(SEL.flavourSwatch + ', ' + SEL.weightSwatch))) {
        var sh = handleOf(el.getAttribute('href'));
        var sib = siblings[sh] || {};
        var isWeight = el.matches(SEL.weightSwatch);
        push('Variant Selected', {
          'Product ID': product.id, 'Variant ID': sib.variantId || sh,
          'Flavour': isWeight ? product.flavour : (txt(el, 40) || sib.flavour), 'Pack Size': isWeight ? (txt(el.querySelector(SEL.weightLabel), 20) || sib.packSize) : product.packSize,
          'Price': sib.price
        });
        if (sib.available === false) push('Product Out of Stock Viewed', { 'Product ID': sib.id || sh, 'SKU': sib.sku, 'Flavour': sib.flavour, 'Pack Size': sib.packSize, 'Source Screen': 'PDP' });
        return;
      }
      if ((el = target.closest(SEL.thumbnail))) {
        var pos = parseInt(el.getAttribute('data-media-position'), 10) || indexIn(el.parentElement.children, el);
        push('Product Image Viewed', { 'Product ID': product.id, 'Image Number': pos });
        return;
      }
      if ((el = target.closest(SEL.accordionButton))) {
        var label = txt(el, 120);
        if (/ingredient/i.test(label)) push('Ingredients Viewed', { 'Product ID': product.id });
        else if (/nutrition|nutrient/i.test(label)) push('Nutrition Information Viewed', { 'Product ID': product.id });
        else if (/benefit/i.test(label)) push('Benefits Viewed', { 'Product ID': product.id });
        else if (/guarantee/i.test(label)) push('Guarantee Viewed', { 'Product ID': product.id, 'Guarantee Type': label });
        else if (/about the product|more information/i.test(label)) { /* covered by GA4 phase 2; no sheet event */ }
        else push('FAQ Viewed', { 'Product ID': product.id, 'Question': label });
        return;
      }
      if ((el = target.closest(SEL.ratingBreakdown))) { push('Rating Breakdown Viewed', { 'Product ID': product.id }); return; }
      if ((el = target.closest(SEL.reviewWrite))) { push('Review Started', { 'Product ID': product.id }); return; }
      if ((el = target.closest(SEL.pincodeButton))) {
        pendingPincode = (document.querySelector(SEL.pincodeInput) || {}).value || ''; pincodeArmed = true; return;
      }
      // EasyGift bundle widget (client-rendered, maybe open shadow DOM) — best effort
      if (bundle.active && e.composedPath) {
        var path = e.composedPath();
        for (var i = 0; i < path.length; i++) {
          var n = path[i];
          if (!n || n.nodeType !== 1) continue;
          var sig = ((typeof n.className === 'string' ? n.className : '') + ' ' + (n.id || '')).toLowerCase();
          if (/easygift|eg-|bundle/.test(sig) && /add|select|choose|pick|item|card|product/.test(sig)) {
            var pid = n.getAttribute('data-product-id') || n.getAttribute('data-variant-id') || handleOf((n.querySelector && n.querySelector('a[href*="/products/"]') || {}).getAttribute ? n.querySelector('a[href*="/products/"]').getAttribute('href') : '');
            push('Bundle Product Added', { 'Bundle ID': bundle.id, 'Product ID': pid || txt(n, 60), 'Position': n.parentElement ? indexIn(n.parentElement.children, n) : undefined });
            break;
          }
        }
      }
    }

    // --- Blog CTA ---
    if (pageType() === 'article' && (el = target.closest(SEL.contentCta))) {
      push('Content CTA Clicked', { 'Content ID': location.pathname, 'Content Type': 'Blog', 'CTA': txt(el, 60), 'Destination': abs(el.getAttribute('href')) });
      return;
    }

    // --- Wishlist ---
    if ((el = target.closest(SEL.wishlistIcon)) && el.closest(SEL.header)) {
      setTimeout(function () { push('Wishlist Viewed', { 'Item Count': document.querySelectorAll('.wishlist-item, [class*="wishlist-product"]').length }); }, 600);
    }
  }

  /* ======================================================================
   * SEARCH (Searchtap overlay + /search page)
   * ==================================================================== */
  var lastSearchTerm = '';
  function currentSearchTerm() {
    var q = document.querySelector('.st-search-query');
    var input = document.querySelector('.st-search-input:focus, .st-search-input');
    return txt(q, 100) || (input && input.value) || query('q') || lastSearchTerm;
  }
  function currentCategory() {
    return (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || (pageType() === 'search' ? 'search' : currentSearchTerm() ? 'search' : '');
  }
  function activeFilters() {
    return Array.prototype.map.call(document.querySelectorAll('.st-filter-tags .st-filter-value, .active-facets__button, .st-selected-filter'), function (el) { return txt(el, 40); }).filter(Boolean).join(',').slice(0, 200);
  }
  function trackSearchResults(source) {
    var term = currentSearchTerm();
    if (!term || term === lastSearchTerm) return;
    var results = document.querySelector(SEL.searchResults);
    var noRes = document.querySelector(SEL.searchNoResults);
    var count = results ? results.querySelectorAll(SEL.searchResultCard).length : 0;
    var declared = money(txt(document.querySelector(SEL.searchCount)));
    if (!results && !noRes && source !== 'page') return;
    lastSearchTerm = term;
    var oos = results ? results.querySelectorAll(SEL.searchOos).length : 0;
    var total = declared !== undefined ? declared : count;
    if (CONFIG.pluginOverlap !== 'plugin') push('Product Searched', { 'Search Term': term, 'Result Count': total, 'Out of Stock Count': oos });
    if (!total || noRes) push('Search No Results', { 'Search Term': term });
  }
  function watchSearch() {
    if (!window.MutationObserver) return;
    var onMut = debounce(function () { trackSearchResults('overlay'); }, 700);
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var t = muts[i].target;
        if (t && t.closest && t.closest('.st-results-list, .st-no-results, .st-autocomplete-content, .st-right-panel, [class*="st-results"], .template-search__results')) { onMut(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches(SEL.searchInput)) { lastSearchTerm = ''; setTimeout(function () { trackSearchResults('enter'); }, 900); }
    }, true);
    // Sort <select> on collection / search pages
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('#SortBy, .facet-filters__sort, select[name="sort_by"]')) push('Sort Applied', { 'Sort Type': t.options && t.selectedIndex >= 0 ? t.options[t.selectedIndex].text : t.value, 'Category': currentCategory() });
      if (t && t.matches && t.matches(SEL.variantInputs) && product) {
        var vid = t.value;
        var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
        var v = null; (meta && meta.product && meta.product.variants || []).forEach(function (x) { if (String(x.id) === String(vid)) v = x; });
        var parsed = parseTitle(v ? v.name : '');
        push('Variant Selected', { 'Product ID': product.id, 'Variant ID': String(vid), 'Flavour': parsed.flavour || product.flavour, 'Pack Size': parsed.packSize || product.packSize, 'Price': v ? paise(v.price) : undefined });
      }
    }, true);
  }

  /* ======================================================================
   * LOGIN (KwikPass) — window events + storage
   * ==================================================================== */
  function trackLogin() {
    var wasLoggedIn = !!(storage('kp_customer_id') || storage('kp_token'));
    if (wasLoggedIn) identify(storage('kp-login-no') || storage('notify_phone_number'), { 'KP Customer ID': storage('kp_customer_id') });

    window.addEventListener('kpPopup', function (e) {
      if (e.detail && e.detail.status) push('Login Started', { 'Method': 'Phone', 'Source Screen': screenName() });
    });
    window.addEventListener('user-loggedin', function () {
      var phone = storage('kp-login-no') || storage('notify_phone_number');
      var state = storage('kp_customer_state');
      var isNew = /^n/i.test(state) && !/enabled/i.test(state);
      push('Login', { 'Method': 'Phone', 'Status': 'Success' });
      if (isNew) push('Sign Up', { 'Register Option': 'Phone', 'Status': 'Success' });
      identify(phone, { 'KP Customer ID': storage('kp_customer_id') });
      wasLoggedIn = true;
    });
    // Fallback: storage polling catches a login that finished without the event (e.g. SSO)
    setInterval(function () {
      var now = !!(storage('kp_customer_id') || storage('kp_token'));
      if (now && !wasLoggedIn) {
        wasLoggedIn = true;
        push('Login', { 'Method': 'Phone', 'Status': 'Success' });
        identify(storage('kp-login-no') || storage('notify_phone_number'), { 'KP Customer ID': storage('kp_customer_id') });
      }
    }, 2000);
  }

  /* ======================================================================
   * CART DRAWER / CHECKOUT (GoKwik globals)
   * ==================================================================== */
  function trackCheckoutFlow() {
    wrapGlobal('openGokwikSideCart', function () { cartViewed('Cart Drawer'); });
    wrapGlobal('gokwikCheckoutClickFn', function () {
      if (Date.now() - lastCheckoutClick > 1500) {
        lastCheckoutClick = Date.now();
        var cp = cartProps();
        push('Checkout Button Clicked', { 'Cart ID': cp['Cart ID'], 'Cart Value': cp['Cart Value'], 'Item Count': cp['Item Count'] });
      }
    });
    wrapGlobal('gokwikBuyNowClickFn', function () {
      if (!product) return;
      push('Buy Now Clicked', { 'Product ID': product.id, 'SKU': product.sku, 'Variant': product.variantTitle || product.variantId, 'Quantity': 1, 'Price': product.price, 'Source Screen': screenName() });
    });
    // Checkout Started = GoKwik checkout iframe mounted
    if (window.MutationObserver) {
      var seenAt = 0;
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            var f = n.matches && n.matches('iframe[src*="gokwik"], .checkout-on-cart-iframe, #gokwik-checkout-iframe') ? n : (n.querySelector && n.querySelector('iframe[src*="gokwik.co"]'));
            if (f && Date.now() - seenAt > 30000) {
              seenAt = Date.now();
              var cp = cartProps();
              push('Checkout Started', { 'Checkout ID': '', 'Cart ID': cp['Cart ID'], 'Order Value': cp['Cart Value'], 'Item Count': cp['Item Count'], 'Coupon Code': cp['Coupon Code'] });
            }
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
    // Coupon UI (drawer / cart page) — entered; applied/failed come from the GoKwik mirror
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches(SEL.couponInput) && once('coupon-viewed:' + Math.floor(Date.now() / 60000))) push('Coupon Viewed', { 'Cart Value': cartProps()['Cart Value'] });
    }, true);
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (e.key === 'Enter' && t && t.matches && t.matches(SEL.couponInput) && t.value) push('Coupon Entered', { 'Coupon Code': t.value.trim(), 'Cart Value': cartProps()['Cart Value'] });
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest && e.target.closest('button, [role="button"]');
      if (!t) return;
      var input = t.closest('form, div') && t.closest('form, div').querySelector(SEL.couponInput);
      if (input && input.value && /apply/i.test(txt(t, 20))) push('Coupon Entered', { 'Coupon Code': input.value.trim(), 'Cart Value': cartProps()['Cart Value'] });
    }, true);
    // Free Gift Viewed — threshold messaging in the drawer ("Add ₹X more to unlock…")
    if (window.MutationObserver) {
      var giftSeen = debounce(function (node) {
        var t = txt(node, 200);
        var m = t.match(/add\s*₹?\s*([\d,]+)\s*more/i);
        if (!m) return;
        var remaining = money(m[1]);
        var cp = cartProps();
        if (once('gift-viewed:' + remaining)) push('Free Gift Viewed', { 'Gift ID': 'threshold', 'Gift Name': t.slice(0, 80), 'Threshold': cp['Cart Value'] !== undefined && remaining !== undefined ? +(cp['Cart Value'] + remaining).toFixed(2) : undefined, 'Cart Value': cp['Cart Value'], 'Remaining To Unlock': remaining });
      }, 500);
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var t = muts[i].target;
          if (t && t.nodeType === 1 && /more/i.test(t.textContent || '') && /₹/.test(t.textContent || '') && t.closest && t.closest('[class*="gokwik"], [class*="kwik"], [class*="cart"]')) { giftSeen(t); return; }
        }
      }).observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  /* ======================================================================
   * OFFERS + TRUST — view events on the homepage
   * ==================================================================== */
  function trackOffersAndTrust() {
    observeAll(SEL.bannerSlide, 'Offer Viewed', function (el) {
      return { 'Offer ID': el.getAttribute('data-banner-id') || el.getAttribute('data-banner-name') || '', 'Offer Name': el.getAttribute('data-banner-name') || altOf(el), 'Offer Type': 'Banner', 'Placement': 'Home Hero', 'Position': parseInt(el.getAttribute('data-banner-index'), 10) || undefined };
    }, 'banner');
    observe(document.querySelector(SEL.qualityCerts), 'Trust Content Viewed', function () { return { 'Content Type': 'Quality', 'Content ID': 'quality-stands-apart', 'Placement': 'Home' }; }, 'quality');
    observe(document.querySelector(SEL.pressLogos), 'Trust Content Viewed', function () { return { 'Content Type': 'Featured & Trusted', 'Content ID': 'featured-trusted', 'Placement': 'Home' }; }, 'press');
    var g = document.querySelector(SEL.guarantee);
    if (g) {
      observe(g, 'Guarantee Viewed', function () { return { 'Product ID': product ? product.id : '', 'Guarantee Type': '10 Lakh Guarantee' }; });
      observe(g, 'Trust Content Viewed', function () { return { 'Content Type': 'Guarantee', 'Content ID': '10-lakh-guarantee', 'Placement': screenName() }; }, 'guarantee-trust');
    }
  }

  /* ======================================================================
   * REVIEWS (Judge.me) / VIDEO / BACK IN STOCK
   * ==================================================================== */
  function trackForms() {
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || !f.matches) return;
      if (f.matches(SEL.reviewForm) && product) {
        var r = f.querySelector(SEL.reviewRating);
        var rating = r ? parseInt(r.value, 10) : f.querySelectorAll('.jdgm-star.jdgm--on').length;
        var files = Array.prototype.some.call(f.querySelectorAll('input[type="file"]'), function (i) { return i.files && i.files.length; });
        push('Product Rated/Reviewed', { 'Product ID': product.id, 'Rating': rating || undefined, 'Review Type': files ? 'Photo' : 'Text' });
      }
      if (f.matches(SEL.backInStock) && product) {
        push('Back in Stock Notification Requested', { 'Product ID': product.id, 'Variant': product.variantTitle || product.variantId });
      }
    }, true);
  }
  function trackVideo() {
    var started = {};
    document.addEventListener('play', function (e) {
      var v = e.target; if (!v || v.tagName !== 'VIDEO') return;
      var id = v.currentSrc || v.src || v.id || 'video';
      if (started[id]) return; started[id] = Date.now();
      if (product) push('Product Video Started', { 'Product ID': product.id });
    }, true);
    document.addEventListener('ended', function (e) {
      var v = e.target; if (!v || v.tagName !== 'VIDEO') return;
      var id = v.currentSrc || v.src || v.id || 'video';
      if (product) push('Product Video Completed', { 'Product ID': product.id });
      push('Video Viewed', { 'Video ID': id.split('/').pop().slice(0, 80), 'Watch Duration': Math.round(v.duration || 0), 'Completion': true });
      delete started[id];
    }, true);
    window.addEventListener('pagehide', function () {
      Array.prototype.forEach.call(document.querySelectorAll('video'), function (v) {
        var id = v.currentSrc || v.src || v.id || 'video';
        if (started[id] && v.currentTime > 1) push('Video Viewed', { 'Video ID': id.split('/').pop().slice(0, 80), 'Watch Duration': Math.round(v.currentTime), 'Completion': false });
      });
    });
  }

  /* ======================================================================
   * BOOT
   * ==================================================================== */
  interceptCartRequests();          // must run before other scripts fire cart requests
  document.addEventListener('click', onClick, true);

  function boot() {
    product = loadProduct();
    syncCart('init', true);          // snapshot only, no diff
    trackLogin();
    trackCheckoutFlow();
    trackPageViews();
    withProductJson(trackPdp);
    trackOffersAndTrust();
    watchSearch();
    trackForms();
    trackVideo();
    if (DEBUG) {
      console.log('[BL CT] ready', { page: pageType(), product: product, overlap: CONFIG.pluginOverlap });
      if (window.clevertapApp) console.warn('[BL CT] CleverTap Shopify app detected. With pluginOverlap="file", disable its Product Viewed / Added To Cart / Removed from Cart / Searched Product in CleverTap → Settings → Shopify → Customize Events.');
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.BL_CT = { version: '1.0.0', config: CONFIG, selectors: SEL, push: push, identify: identify, cart: function () { return cart; }, product: function () { return product; }, syncCart: syncCart };
})();